import React, { useEffect, useState, useCallback, memo } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   Box,
   VStack,
   Text,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// redux action
import { getSetupFormat, setupFormatCustomQuery } from '../../redux/actions/setupFormat';

// context
import { useUser } from 'contexts';

// UTILS
import { handlePrintClick, injectDataIntoHtml } from 'utils/util';

const SelectableList = memo(({ formats, selectedIndex, onSelect }) => {
   const handleKeyDown = useCallback(
      (e) => {
         if (e.key === 'ArrowDown') {
            onSelect((prev) => (prev + 1) % formats?.length);
         } else if (e.key === 'ArrowUp') {
            onSelect((prev) => (prev - 1 + formats?.length) % formats?.length);
         }
      },
      [formats?.length, onSelect],
   );

   useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [formats?.length, handleKeyDown]);

   return (
      <VStack align="stretch" spacing={2}>
         {formats?.map((item, index) => (
            <Box
               key={index}
               px={4}
               py={2}
               bg={index === selectedIndex ? 'blue.500' : 'gray.100'}
               color={index === selectedIndex ? 'white' : 'black'}
               borderRadius="md"
               cursor="pointer"
               onClick={() => onSelect(index)}>
               <Text>{item?.PrintFormat?.Name}</Text>
            </Box>
         ))}
      </VStack>
   );
});

const PrintFormat = ({ isOpen, onClose, daybook, functionName, redirect }) => {
   const { user } = useUser();
   const dispatch = useDispatch();
   const [selectedIndex, setSelectedIndex] = useState(0);
   const navigate = useNavigate();

   const prints = useSelector((state) => state.setupPrint.list);

   const getSetupFormats = useCallback(async () => {
      if (isOpen && user?.loginhistory?.companyId && daybook?.id) {
         await dispatch(
            getSetupFormat({
               companyId: user?.loginhistory?.companyId,
               daybookId: daybook?.id,
               functionName: functionName,
            }),
         );
      }
   }, [dispatch, isOpen]);

   const handlePrint = useCallback(async () => {
      const selectedItem = prints[selectedIndex];

      const result = await dispatch(
         setupFormatCustomQuery({
            query: selectedItem?.PrintFormat?.query,
         }),
      );
      if (result && result?.data && result?.data?.length > 0) {
         const html = injectDataIntoHtml(selectedItem?.PrintFormat?.html, result.data);
         handlePrintClick({}, html, selectedItem?.PrintFormat?.css, navigate, redirect);
      }
   }, [dispatch, navigate, prints, redirect, selectedIndex]);

   useEffect(() => {
      getSetupFormats();
   }, [getSetupFormats]);

   useEffect(() => {
      if (prints?.length > 0) {
         const defaultIndex = prints.findIndex((item) => item?.isDefault === true);
         setSelectedIndex(defaultIndex !== -1 ? defaultIndex : 0);
      }
   }, [prints]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         if (e.key === 'Enter' && isOpen) {
            if (prints.length > 0) {
               handlePrint();
            } else {
               onClose();
               navigate(redirect);
            }
         }
         if (e.key === 'Esc' && isOpen) {
            onClose();
            navigate(redirect);
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [handlePrint, isOpen]);

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
         <ModalOverlay />
         <ModalContent>
            <ModalHeader>Print Format</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <SelectableList formats={prints} selectedIndex={selectedIndex} onSelect={setSelectedIndex} />
            </ModalBody>

            <ModalFooter>
               <Button colorScheme="red" mr={3} onClick={onClose}>
                  Close
               </Button>
               <Button variant="solid" colorScheme="green" onClick={() => handlePrint()}>
                  Print
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default PrintFormat;
